import { authFetch } from "@/lib/authFetch";
import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, FolderKanban, ChevronDown, ChevronRight, Trash2, Loader2,
  Circle, Clock, Pause, CheckCircle2, X, GripVertical,
} from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import ProjetoKanban from "./ProjetoKanban";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Sessão UI-2 — ProjetosTab
//
// Lista de projetos expansíveis. Cada projeto card colapsado mostra
// progress bar global; expandido mostra Kanban 4-colunas (ProjetoKanban,
// componente separado pra encapsular DnD em T5).
//
// 4 colunas confirmadas em decisão: todo / doing / paused / done.
// Não consolidar em 3 (paused é funcional).

export const STAGES = [
  { id: "todo",   label: "A fazer",     icon: Circle,        dot: "bg-foreground-subtle", color: "text-foreground-muted" },
  { id: "doing",  label: "Em progresso", icon: Clock,         dot: "bg-brand-cyan",        color: "text-brand-cyan" },
  { id: "paused", label: "Pausado",     icon: Pause,         dot: "bg-status-warning",    color: "text-status-warning" },
  { id: "done",   label: "Concluído",   icon: CheckCircle2,  dot: "bg-brand-green",       color: "text-brand-green" },
];

function CreateProjectModal({ open, onClose, onSubmit }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [endDate, setEndDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) { setTitle(""); setDescription(""); setEndDate(""); } }, [open]);

  if (!open) return null;
  const handleSubmit = async (e) => {
    e.preventDefault(); if (!title.trim()) return;
    setSubmitting(true);
    try { await onSubmit({ title: title.trim(), description: description.trim() || null, end_date: endDate || null }); onClose(); }
    finally { setSubmitting(false); }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="project-modal-title"
      >
        <motion.div
          initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-surface-overlay border border-subtle rounded-2xl p-6 safe-modal-pb w-full max-w-md"
        >
          <div className="flex items-center justify-between mb-5">
            <h3 id="project-modal-title" className="font-display font-semibold text-h2 text-foreground">Novo projeto</h3>
            <button type="button" onClick={onClose} aria-label="Fechar" className="p-1 rounded-md text-foreground-muted hover:text-foreground hover:bg-surface-card-hover">
              <X className="w-4 h-4" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-caption text-foreground-muted mb-1.5 uppercase" style={{ letterSpacing: "0.08em" }}>Nome</label>
              <input
                autoFocus type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Lançamento do app"
                className="w-full px-4 py-3 bg-surface-card border border-subtle rounded-lg text-foreground placeholder-foreground-subtle focus:outline-none focus:border-brand-cyan/50"
              />
            </div>
            <div>
              <label className="block text-caption text-foreground-muted mb-1.5 uppercase" style={{ letterSpacing: "0.08em" }}>Descrição</label>
              <textarea
                value={description} onChange={(e) => setDescription(e.target.value)}
                rows={2} placeholder="Detalhes do projeto…"
                className="w-full px-4 py-3 bg-surface-card border border-subtle rounded-lg text-foreground placeholder-foreground-subtle focus:outline-none focus:border-brand-cyan/50 resize-none"
              />
            </div>
            <div>
              <label className="block text-caption text-foreground-muted mb-1.5 uppercase" style={{ letterSpacing: "0.08em" }}>Prazo (opcional)</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-3 bg-surface-card border border-subtle rounded-lg text-foreground focus:outline-none focus:border-brand-cyan/50" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={onClose} className="flex-1">Cancelar</Button>
              <Button type="submit" variant="primary" disabled={!title.trim() || submitting} className="flex-1">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Criar
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function ProjectRow({ project, expanded, onToggle, onDelete, onTasksChange }) {
  const total = project.task_count ?? project.tasks?.length ?? 0;
  const done = project.done_count ?? project.tasks?.filter(t => t.stage === "done").length ?? 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <Card variant="elevated" className="glass-card glow-hover overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left flex items-center gap-4 p-4 md:p-5 hover:bg-surface-card-hover transition-colors"
        aria-expanded={expanded}
        aria-controls={`project-kanban-${project.id}`}
        data-testid={`project-row-${project.id}`}
      >
        <div className="w-10 h-10 rounded-lg bg-brand-cyan/10 flex items-center justify-center shrink-0">
          <FolderKanban className="w-5 h-5 text-brand-cyan" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h3
              className="font-display font-semibold text-foreground text-h3 truncate uppercase"
              style={{ letterSpacing: "0.02em" }}
            >
              {project.title || project.name || "(sem nome)"}
            </h3>
          </div>
          <div className="mt-2 flex items-center gap-3">
            <div className="flex-1 max-w-md">
              <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
                  className="h-full bg-gradient-to-r from-brand-cyan to-brand-green rounded-full"
                />
              </div>
            </div>
            <span className="text-caption text-foreground-muted shrink-0 tabular-nums">
              {done}/{total} · {pct}%
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(project); }}
          aria-label={`Excluir projeto ${project.title}`}
          className="p-1.5 rounded-md text-foreground-subtle hover:text-status-error hover:bg-status-error/10 transition-colors opacity-0 group-hover:opacity-100"
          data-testid={`project-delete-${project.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
        {expanded ? (
          <ChevronDown className="w-5 h-5 text-foreground-muted" aria-hidden="true" />
        ) : (
          <ChevronRight className="w-5 h-5 text-foreground-muted" aria-hidden="true" />
        )}
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            id={`project-kanban-${project.id}`}
            key="kanban"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            className="overflow-hidden border-t border-subtle"
          >
            <div className="p-4 md:p-5">
              <ProjetoKanban project={project} onTasksChange={onTasksChange} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

export default function ProjetosTab() {
  const { user } = useAuth();
  const userId = user?.id;
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const [modalOpen, setModalOpen] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const r = await authFetch(`${API_URL}/api/projects`);
      if (r.ok) {
        const data = await r.json();
        const list = Array.isArray(data?.projects) ? data.projects : [];
        const mine = userId ? list.filter(p => !p.userId || p.userId === userId) : list;
        setProjects(mine);
      }
    } catch (_) { /* ignore */ }
    finally { setLoading(false); }
  }, [userId]);

  // Re-fetch projeto específico (após mudança de tasks via Kanban)
  const refetchProject = useCallback(async (projectId) => {
    try {
      const r = await authFetch(`${API_URL}/api/projects/${projectId}`);
      if (r.ok) {
        const data = await r.json();
        setProjects(prev => prev.map(p => p.id === projectId ? { ...p, ...data } : p));
      }
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const toggleExpanded = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleCreate = async (data) => {
    try {
      const r = await authFetch(`${API_URL}/api/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, ...(userId ? { user_id: userId } : {}) }),
      });
      if (r.ok) {
        toast.success("Projeto criado");
        await fetchProjects();
      } else toast.error("Erro ao criar projeto");
    } catch (_) { toast.error("Erro ao criar projeto"); }
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`Excluir "${project.title}" e todas as suas tarefas?`)) return;
    try {
      const r = await authFetch(`${API_URL}/api/projects/${project.id}`, { method: "DELETE" });
      if (r.ok) {
        setProjects(prev => prev.filter(p => p.id !== project.id));
        setExpanded(prev => { const next = new Set(prev); next.delete(project.id); return next; });
        toast.success("Projeto excluído");
      }
    } catch (_) { /* ignore */ }
  };

  const totalProjects = projects.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-h3 text-foreground">
          {totalProjects} projeto{totalProjects === 1 ? "" : "s"}
        </h2>
        <Button
          variant="primary"
          size="md"
          onClick={() => setModalOpen(true)}
          className="bg-brand-cyan/15 border border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/25 hover:shadow-glow-cyan"
          data-testid="project-create-btn"
        >
          <Plus className="w-4 h-4" />
          Novo projeto
        </Button>
      </div>

      {loading ? (
        <ul className="space-y-3" aria-label="Carregando projetos" data-testid="project-list-skeleton">
          {[0, 1].map(i => (
            <li
              key={i}
              className="h-[88px] rounded-xl border border-subtle bg-surface-card animate-pulse"
            />
          ))}
        </ul>
      ) : projects.length === 0 ? (
        <Card variant="elevated" className="glass-card py-12 px-8 flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-full bg-brand-cyan/10 flex items-center justify-center mb-4">
            <FolderKanban className="w-6 h-6 text-brand-cyan" />
          </div>
          <h3 className="font-display font-semibold text-h2 text-foreground mb-2">Sem projetos ativos</h3>
          <p className="text-body text-foreground-muted max-w-sm mb-5">
            Organize iniciativas grandes em projetos com etapas e tarefas.
          </p>
          <Button variant="primary" size="md" onClick={() => setModalOpen(true)} data-testid="project-empty-cta">
            <Plus className="w-4 h-4" />
            Criar primeiro projeto
          </Button>
        </Card>
      ) : (
        <ul className="space-y-3" data-testid="project-list">
          <AnimatePresence mode="popLayout">
            {projects.map(p => (
              <motion.li
                key={p.id}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="group"
              >
                <ProjectRow
                  project={p}
                  expanded={expanded.has(p.id)}
                  onToggle={() => toggleExpanded(p.id)}
                  onDelete={handleDelete}
                  onTasksChange={() => refetchProject(p.id)}
                />
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>
      )}

      <CreateProjectModal open={modalOpen} onClose={() => setModalOpen(false)} onSubmit={handleCreate} />
    </div>
  );
}
